import { Star } from 'lucide-react';
import type { ArticleListItem } from '@/hooks/use-articles';
import { cn } from '@/lib/utils';
import { ArticleThumbnail } from './ArticleThumbnail';
import { useUsableArticleImage } from './article-image';
import { deriveArticleRow, type FeedMetaMap } from './article-row';
import { RowActions, type RowToggle } from './RowActions';

// Minimum natural size for an article image to be shown as cover art without
// upscaling into blur. Below this we show no image (never the feed favicon). The
// card hero fills the card width; the magazine tile is small (128x96), so its
// floor is lower - but both comfortably reject favicon-sized icons.
const CARD_IMAGE_MIN = { w: 320, h: 180 };
const MAGAZINE_IMAGE_MIN = { w: 160, h: 120 };

export interface ViewProps {
  items: ArticleListItem[];
  feeds: FeedMetaMap;
  selectedId: string | null;
  focusedId: string | null;
  onSelect: (article: ArticleListItem) => void;
  registerRow: (id: string) => (el: HTMLElement | null) => void;
  /** Star and read toggles from the row itself (#32). No actions without it. */
  onToggle?: RowToggle;
}

/**
 * Entrance stagger, capped so a long page does not cascade forever. Motion is
 * deliberately small and only on mount/view change; `motion-reduce` disables it.
 */
const stagger = (index: number) => ({
  animationDelay: index < 12 ? `${index * 25}ms` : undefined,
});

const ENTER = 'animate-in fade-in slide-in-from-bottom-1 fill-mode-backwards motion-reduce:animate-none';

// On the <li>, not the tile button: a dimmed read tile must not dim its ring.
const focusRing = (focused: boolean) => focused && 'ring-2 ring-ring';

/**
 * A read tile steps back as a whole (text, image and excerpt), so read and
 * unread tell apart at a glance. Every read tile looks the same, the
 * keyboard-focused one too; only the open article stays at full strength.
 * Not on hover or focus-within: after "Mark as read" the pointer and the
 * focus are still on the tile, so it never dimmed.
 */
const readDim = (isRead: boolean, open: boolean) => isRead && !open && 'opacity-50 grayscale';

// --- List --------------------------------------------------------------
// Two-line rows at comfortable density; the `compact:` variant collapses them
// to a single dense title-only line (SPEC-016 replaces the old Compact view).

export function ListView({
  items,
  feeds,
  selectedId,
  focusedId,
  onSelect,
  registerRow,
  onToggle,
}: ViewProps) {
  return (
    <ul role="listbox" aria-label="Articles">
      {items.map((article, index) => {
        const row = deriveArticleRow(article, feeds);
        const focused = focusedId === article.id;
        return (
          <li
            key={article.id}
            role="option"
            aria-selected={focused}
            ref={registerRow(article.id)}
            className={cn('group/row relative', focusRing(focused))}
          >
            <button
              type="button"
              onClick={() => onSelect(article)}
              style={stagger(index)}
              data-read={row.isRead}
              className={cn(
                'flex w-full items-start gap-2 border-b px-3 py-2.5 text-left',
                'transition-colors duration-200 motion-reduce:transition-none',
                'compact:items-center compact:py-1.5',
                // Room for the always-visible touch "..." button.
                onToggle && 'pointer-coarse:pr-12',
                selectedId === article.id ? 'bg-accent' : 'hover:bg-accent/60',
                readDim(row.isRead, selectedId === article.id),
                ENTER,
              )}
            >
              <span
                aria-hidden
                className={cn(
                  'mt-1.5 size-2 shrink-0 rounded-full transition-colors duration-300 motion-reduce:transition-none',
                  'compact:mt-0 compact:size-1.5',
                  row.isRead ? 'bg-transparent' : 'bg-primary',
                )}
              />
              <span className="min-w-0 flex-1">
                <span
                  className={cn(
                    'block truncate text-sm',
                    row.isRead ? 'text-muted-foreground' : 'font-medium',
                  )}
                >
                  {row.title}
                </span>
                {/* Meta line: hidden in compact density for a one-line row. */}
                <span className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground compact:hidden">
                  <span className="truncate">{row.feedName}</span>
                  <span className="shrink-0">{row.when}</span>
                  {row.isStarred && <Star className="size-3 shrink-0 fill-primary text-primary" />}
                </span>
              </span>
              {/* Starred marker kept visible when the meta line is hidden. */}
              {row.isStarred && (
                <Star className="hidden size-3 shrink-0 fill-primary text-primary compact:inline" />
              )}
            </button>
            {onToggle && (
              <RowActions article={article} onToggle={onToggle} className="right-2 top-1/2 -translate-y-1/2" />
            )}
          </li>
        );
      })}
    </ul>
  );
}

// --- Cards (square, hover reveal) ---------------------------------------

/**
 * A square card whose inner stack slides on hover/focus, within the card's own
 * fixed bounds: the image scrolls up and out, the text rides up into its place,
 * and the excerpt fades in below. Nothing reflows; no neighbor moves.
 */
function Card({
  article,
  feeds,
  index,
  selected,
  focused,
  onSelect,
  registerRow,
  onToggle,
}: {
  article: ArticleListItem;
  feeds: FeedMetaMap;
  index: number;
  selected: boolean;
  focused: boolean;
  onSelect: (a: ArticleListItem) => void;
  registerRow: ViewProps['registerRow'];
  onToggle?: RowToggle;
}) {
  const row = deriveArticleRow(article, feeds);
  const showImage = useUsableArticleImage(row.imageUrl, CARD_IMAGE_MIN.w, CARD_IMAGE_MIN.h);

  return (
    // The hover slide keys on the whole card (group/row), so moving onto the
    // quick actions does not drop it back.
    <li
      role="option"
      aria-selected={focused}
      ref={registerRow(article.id)}
      className={cn('group/row relative rounded-lg', focusRing(focused))}
    >
      <button
        type="button"
        onClick={() => onSelect(article)}
        style={stagger(index)}
        data-read={row.isRead}
        className={cn(
          'group relative block aspect-square w-full overflow-hidden rounded-lg border text-left',
          'transition-shadow duration-200 hover:shadow-md motion-reduce:transition-none',
          selected && 'bg-accent',
          readDim(row.isRead, selected),
          ENTER,
        )}
      >
        {/* The sliding stack: the image (16/10 of a square card = 62.5% of its
            height) over a text panel exactly one card tall. On hover/focus it
            translates up by the image height (62.5 / 162.5 of the stack), so
            the text panel then fills the whole card. */}
        <span
          className={cn(
            'absolute inset-x-0 top-0 flex flex-col',
            'transition-transform duration-300 ease-out motion-reduce:transition-none',
            showImage
              ? 'h-[162.5%] group-hover/row:-translate-y-[38.4615%] group-focus-visible:-translate-y-[38.4615%]'
              : 'h-full',
          )}
        >
          {showImage && row.imageUrl && (
            <ArticleThumbnail
              imageUrl={row.imageUrl}
              className="aspect-[16/10] w-full shrink-0 rounded-none"
            />
          )}
          <span className="flex min-h-0 flex-1 flex-col bg-background p-3">
            <span className="flex items-center gap-2 text-xs text-muted-foreground">
              <span className="truncate">{row.feedName}</span>
              <span className="shrink-0">{row.when}</span>
              {row.isStarred && (
                <Star className="ml-auto size-3 shrink-0 fill-primary text-primary" />
              )}
            </span>
            <span
              className={cn(
                'mt-1 line-clamp-2 text-sm',
                row.isRead ? 'text-muted-foreground' : 'font-medium',
              )}
            >
              {row.title}
            </span>
            {row.excerpt && (
              <span
                className={cn(
                  // Fill whatever height is left, fading out at the bottom
                  // rather than stopping after a fixed number of lines.
                  'mt-2 min-h-0 flex-1 overflow-hidden text-xs text-muted-foreground',
                  '[mask-image:linear-gradient(to_bottom,black_75%,transparent)]',
                  // Below the fold at rest when there is an image; revealed as
                  // the stack rises. Always visible when there is no image.
                  showImage &&
                    'opacity-0 transition-opacity duration-300 group-hover/row:opacity-100 group-focus-visible:opacity-100 motion-reduce:opacity-100 motion-reduce:transition-none',
                )}
              >
                {row.excerpt}
              </span>
            )}
          </span>
        </span>
        {!row.isRead && (
          <span
            aria-hidden
            className="absolute right-2 top-2 z-10 size-2 rounded-full bg-primary shadow"
          />
        )}
      </button>
      {onToggle && <RowActions article={article} onToggle={onToggle} className="bottom-2 right-2" />}
    </li>
  );
}

export function CardsView({
  items,
  feeds,
  selectedId,
  focusedId,
  onSelect,
  registerRow,
  onToggle,
}: ViewProps) {
  return (
    <ul
      role="listbox"
      aria-label="Articles"
      className="grid grid-cols-1 gap-3 p-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
    >
      {items.map((article, index) => (
        <Card
          key={article.id}
          article={article}
          feeds={feeds}
          index={index}
          selected={selectedId === article.id}
          focused={focusedId === article.id}
          onSelect={onSelect}
          registerRow={registerRow}
          onToggle={onToggle}
        />
      ))}
    </ul>
  );
}

// --- Magazine (two columns of wide rows) --------------------------------

function MagazineRow({
  article,
  feeds,
  index,
  selected,
  focused,
  onSelect,
  registerRow,
  onToggle,
}: {
  article: ArticleListItem;
  feeds: FeedMetaMap;
  index: number;
  selected: boolean;
  focused: boolean;
  onSelect: (a: ArticleListItem) => void;
  registerRow: ViewProps['registerRow'];
  onToggle?: RowToggle;
}) {
  const row = deriveArticleRow(article, feeds);
  const showImage = useUsableArticleImage(row.imageUrl, MAGAZINE_IMAGE_MIN.w, MAGAZINE_IMAGE_MIN.h);

  return (
    <li
      role="option"
      aria-selected={focused}
      ref={registerRow(article.id)}
      className={cn('group/row relative rounded-lg', focusRing(focused))}
    >
      <button
        type="button"
        onClick={() => onSelect(article)}
        style={stagger(index)}
        data-read={row.isRead}
        className={cn(
          'flex w-full gap-3 rounded-lg border p-3 text-left',
          'transition-[transform,box-shadow,background-color] duration-200',
          // Keyed on the whole tile, so the lift holds over the quick actions.
          'group-hover/row:-translate-y-0.5 group-hover/row:shadow-md motion-reduce:group-hover/row:translate-y-0',
          selected && 'bg-accent',
          readDim(row.isRead, selected),
          ENTER,
        )}
      >
        {showImage && row.imageUrl && (
          <ArticleThumbnail imageUrl={row.imageUrl} className="h-24 w-32 shrink-0" />
        )}
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2 text-xs text-muted-foreground">
            {!row.isRead && (
              <span aria-hidden className="size-2 shrink-0 rounded-full bg-primary" />
            )}
            <span className="truncate">{row.feedName}</span>
            <span className="shrink-0">{row.when}</span>
            {row.isStarred && <Star className="size-3 shrink-0 fill-primary text-primary" />}
          </span>
          <span
            className={cn(
              'mt-0.5 line-clamp-2 text-sm',
              row.isRead ? 'text-muted-foreground' : 'font-semibold',
            )}
          >
            {row.title}
          </span>
          {row.excerpt && (
            <span className="mt-1 line-clamp-2 text-xs text-muted-foreground">{row.excerpt}</span>
          )}
        </span>
      </button>
      {onToggle && <RowActions article={article} onToggle={onToggle} className="right-2 top-2" />}
    </li>
  );
}

export function MagazineView({
  items,
  feeds,
  selectedId,
  focusedId,
  onSelect,
  registerRow,
  onToggle,
}: ViewProps) {
  return (
    <ul role="listbox" aria-label="Articles" className="grid grid-cols-1 gap-3 p-3 lg:grid-cols-2">
      {items.map((article, index) => (
        <MagazineRow
          key={article.id}
          article={article}
          feeds={feeds}
          index={index}
          selected={selectedId === article.id}
          focused={focusedId === article.id}
          onSelect={onSelect}
          registerRow={registerRow}
          onToggle={onToggle}
        />
      ))}
    </ul>
  );
}
